import {AceLoggerService} from '@logging/ace-logger.service';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {BaseService} from '@shared/services/api/base-service';
import {AceLogger} from '@logging/ace-logger';
import {MeterProvider} from '@app/graphql-typings/graphql';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {StringUtils} from '@shared/util/general/string-utils';

enum METER_PROVIDER_OPERATION {
  PROVIDER= 'meter_provider',
  PROVIDERS = 'meter_providers'
}

export interface MeterProviderStatus {
  meter_provider: MeterProvider;
  status: string;
}

interface MeterProviderAPIDelete {
  provider_id: number;
  deleted: boolean;
}

interface MeterProviderFetchOptions {
  includeExternalIntegrations?: boolean;
}

export const meterProvidersResolver: ResolveFn<MeterProviderStatus> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<MeterProviderStatus> => {
  const status: string = route.paramMap.get('status') ? route.paramMap.get('status') : '';
  const providerId: number = StringUtils.stringToNumber(route.paramMap.get('id'));
  if (providerId == null) {
    throw new Error('Provider ID must be specified');
  }
  return inject(MeterProvidersService).getProvider(+route.paramMap.get('id')).pipe(
    map((provider: MeterProvider) => {
      return {meter_provider: provider, status: status};
    })
  );
};

@Injectable()
export class MeterProvidersService extends BaseService {

  private headers: HttpHeaders = this.getHeaders();
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(MeterProvidersService.name);
  }

  // post a new provider
  postProvider(provider: MeterProvider): Observable<MeterProvider> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/providers', provider, options)
      .pipe(map((response: APIResponse<MeterProvider>) => this.httpUtilityService.extractResponseData(response, METER_PROVIDER_OPERATION.PROVIDER)));
  }

  // update a provider
  putProvider(provider: MeterProvider): Observable<MeterProvider> {
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/providers/' + provider.provider_id, provider, options)
      .pipe(map((response: APIResponse<MeterProvider>) => this.httpUtilityService.extractResponseData(response, METER_PROVIDER_OPERATION.PROVIDER)));
  }

  // Delete a Provider
  deleteProvider(providerId: number): Observable<MeterProviderAPIDelete> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/providers/' + providerId,  options)
      .pipe(map((response: APIResponse<MeterProviderAPIDelete>) => this.httpUtilityService.extractResponseData(response, METER_PROVIDER_OPERATION.PROVIDER)));
  }

  private getMeterProviderFetchParams(fetchOptions?: MeterProviderFetchOptions): object {
    return {
      ...fetchOptions?.includeExternalIntegrations && {
        params: {
          include_external_integrations: true
        }
      }
    };
  }

  getProvider(providerId: number, fetchOptions?: MeterProviderFetchOptions): Observable<MeterProvider> {
    const options = {
      headers: this.headers,
      ...this.getMeterProviderFetchParams(fetchOptions)
    };
    return this.http.get(this.getGateway() + '/providers/' + providerId , options)
      .pipe(map((response: APIResponse<MeterProvider>) => this.httpUtilityService.extractResponseData(response, METER_PROVIDER_OPERATION.PROVIDER)));

  }

  getProviders(fetchOptions?: MeterProviderFetchOptions): Observable<MeterProvider[]> {
    const options = {
      headers: this.headers,
      ...this.getMeterProviderFetchParams(fetchOptions)
    };
    return this.http.get(this.getGateway() + '/providers' , options)
      .pipe(map((response: APIResponse<MeterProvider[]>) => this.httpUtilityService.extractResponseData(response, METER_PROVIDER_OPERATION.PROVIDERS)));
  }
}
